/* import __COLOCATED_TEMPLATE__ from './standalone-checkout.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { keepLatestTask } from 'ember-concurrency-decorators';
import { INTENT_TYPES } from 'embercom/components/stripe/stripe-component';
import { post } from 'embercom/lib/ajax';
import { taskFor } from 'ember-concurrency-ts';
import { PERFORMANCE_PRO_BASE_ID, FIN_FOR_PLATFORMS_BASE_ID } from 'embercom/lib/billing';
import { DEFAULT_REQUEST_PARAMS } from 'embercom/services/quote-service';
import { PricingModelIdentifier } from 'embercom/lib/billing/pricing-models';

import type Router from '@ember/routing/router-service';
import { type TaskGenerator } from 'ember-concurrency';
import type { AfterPaymentMethodSuccess } from 'embercom/components/stripe/stripe-component';
import type QuoteService from 'embercom/services/quote-service';
import { Metric } from 'embercom/models/data/pricing/metric-types';
import moment from 'moment-timezone';
import safeWindowOpen from 'embercom/lib/safe-window-open';

interface Signature {
  Element: any;
}

export default class StandaloneCheckout extends Component<Signature> {
  @service declare appService: AppService;
  @service declare quoteService: QuoteService;
  @service declare customerService: any;
  @service declare intl: any;
  @service declare notificationsService: any;
  @service declare store: any;
  @service declare router: Router;

  @service declare purchaseAnalyticsService: any;

  @tracked selectedPlanId = FIN_FOR_PLATFORMS_BASE_ID;
  @tracked showCreditCardPage = false;
  @tracked hasSelectedPerformanceProAddon = false;

  INTENT_TYPES = INTENT_TYPES;

  constructor(owner: unknown, args: any) {
    super(owner, args);
    this.showCreditCardPage = !this.canSeeAiInsightsAddon;
    taskFor(this.getQuotes).perform();
  }

  get termsAndConditionsCopy() {
    if (this.finEnableOutcomes) {
      return this.intl.t(
        'signup.teams.pricing5.checkouts.fin-standalone.terms-and-conditions-outcome',
        this.defaultTermsAndConditionsTranslationParams,
      );
    } else {
      return this.intl.t(
        'signup.teams.pricing5.checkouts.fin-standalone.terms-and-conditions',
        this.defaultTermsAndConditionsTranslationParams,
      );
    }
  }

  get whatCountsAsResolutionArticleId() {
    return 10772642; // https://fin.ai/help/en/articles/10772642
  }

  get whatCountsAsResolutionURL() {
    return `https://fin.ai/help/en/articles/${this.whatCountsAsResolutionArticleId}`;
  }

  private get defaultTermsAndConditionsTranslationParams() {
    return {
      price: this.intl.formatNumber(this.totalPriceInDollars, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
      baseUsage: this.baseUsage,
      pricePerResolution: this.intl.formatNumber(this.pricePerResolution, {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }),
      renewalDate: this.intl.formatDate(this.renewalDate, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
      termsOfServiceURL: 'https://www.intercom.com/terms-of-service',
      privacyPolicyURL: 'https://www.intercom.com/privacy',
      additionalProductTermsURL:
        'https://www.intercom.com/legal/terms-and-policies/additional-product-terms',
      htmlSafe: true,
    };
  }

  get queryParamsForRedirect() {
    return {
      plan_id: this.selectedPlanId,
      trial_extension: true,
    };
  }

  get selectedPlanIds() {
    let planIds = [Number(this.selectedPlanId)];
    if (this.hasSelectedPerformanceProAddon) {
      planIds.push(Number(PERFORMANCE_PRO_BASE_ID));
    }
    return planIds;
  }

  get redirectUrl() {
    let baseUrl = window.location.origin;
    let url = this.router.urlFor('apps.app.teams-checkout', this.appService.app.id, {
      queryParams: this.queryParamsForRedirect,
    });
    return `${baseUrl}${url}`;
  }

  get loading() {
    return this.quoteService.loading;
  }

  get totalPriceInCents() {
    return this.totalPriceInDollars * 100;
  }

  get totalPriceInDollars() {
    return this.baseCost + this.copilotSeatTotalPrice + this.performanceProAddonTotalPrice || 0;
  }

  get formattedTotalPrice() {
    return this.intl.formatNumber(this.totalPriceInDollars, {
      style: 'currency',
      currency: 'USD',
    });
  }

  get quote() {
    return this.quoteService.getQuoteById([Number(this.selectedPlanId)]);
  }

  get pricePerResolution() {
    return (
      this.quote?.metricPerUnitPrice(
        Number(this.selectedPlanId),
        Metric.resolutions_with_custom_answers,
      ) || 0
    );
  }

  get baseCost() {
    return this.quote?.baseCostInDollars() || 0;
  }

  get copilotSeatTotalPrice() {
    return (
      (this.quote?.getChargeForPlanIdAndMetric(
        Number(this.selectedPlanId),
        Metric.copilot_seat_count,
      )?.price || 0) / 100
    );
  }

  get currentCopilotSeatUsage() {
    return (
      this.quote?.metricActualUsage(Number(this.selectedPlanId), Metric.copilot_seat_count) || 0
    );
  }

  get baseUsage() {
    return (
      this.quote?.metricBaseUsage(
        Number(this.selectedPlanId),
        Metric.resolutions_with_custom_answers,
      ) || 0
    );
  }

  get renewalDate() {
    return moment().add(1, 'month');
  }

  get finEnableOutcomes() {
    return this.appService.app.canUseOutcomesRenaming;
  }

  get showPerformanceProAddonCard() {
    return this.appService.app.canSeeAiInsightsAddon;
  }

  get fullPerformanceProAddonPrice() {
    return this.quote?.fullBreakdownAmount(Number(PERFORMANCE_PRO_BASE_ID)) || 99;
  }

  get performanceProAddonTotalPrice() {
    return this.hasSelectedPerformanceProAddon ? this.fullPerformanceProAddonPrice : 0;
  }

  get canSeeAiInsightsAddon() {
    return this.appService.app.canSeeAiInsightsAddon;
  }

  get showPageDescription() {
    return (this.canSeeAiInsightsAddon && !this.showCreditCardPage) || !this.canSeeAiInsightsAddon;
  }

  @action
  onBackButtonClick() {
    if (this.canSeeAiInsightsAddon && this.showCreditCardPage) {
      this.setShowCreditCardPage();
    } else {
      this.router.transitionTo(this.appService.app.onboardingHomeRoute);
    }
  }

  @action sendAnalyticsEvent({
    action,
    object,
    context,
  }: {
    action: string;
    object: string;
    context?: Record<string, any>;
  }) {
    this.purchaseAnalyticsService.trackEvent({
      action,
      object,
      context,
      place: 'fin-standalone-checkout',
    });
  }

  @action showTooltipArticle(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    this.sendAnalyticsEvent({
      action: 'clicked',
      object: 'fin-guarantee-terms-article',
    });

    safeWindowOpen('https://www.intercom.com/legal/terms-and-policies/fin-guarantee', '_blank');
  }

  @action
  setShowCreditCardPage() {
    if (!this.canSeeAiInsightsAddon) {
      return;
    }
    this.showCreditCardPage = !this.showCreditCardPage;
    if (this.showCreditCardPage) {
      this.sendAnalyticsEvent({
        action: 'clicked',
        object: 'continue_to_payment_button',
        context: {
          plans: this.selectedPlanId,
        },
      });

      this.sendAnalyticsEvent({
        action: 'viewed',
        object: 'fin-standalone-checkout',
      });
    }
  }
  @action toggleAiInsightsAddon() {
    this.hasSelectedPerformanceProAddon = !this.hasSelectedPerformanceProAddon;
    this.sendAnalyticsEvent({
      action: this.hasSelectedPerformanceProAddon ? 'selected' : 'unselected',
      object: 'ai_insights_addon_selector',
    });
  }

  @action showWhatCountsAsResolutionArticle() {
    window.Intercom('showArticle', this.whatCountsAsResolutionArticleId);
    this.sendAnalyticsEvent({
      action: 'clicked',
      object: 'what_counts_as_resolution_article',
    });
  }

  @keepLatestTask
  *convertSubscription(afterPaymentMethodSuccess: AfterPaymentMethodSuccess): TaskGenerator<void> {
    let { paymentMethod, stripeIntent, address, taxNumber } = afterPaymentMethodSuccess;
    let paramsForAnalytics = {
      plan_ids: this.selectedPlanIds,
      payment_intent: stripeIntent?.isPaymentIntent,
    };

    let params = {
      ...paramsForAnalytics,
      app_id: this.appService.app.id,
      stripe_payment_method_id: paymentMethod,
      ...(address.streetAddress &&
        address.countryCode && {
          address: {
            country_code: address.countryCode,
            street_address: address.streetAddress,
            ...(address.stateCode && { state_code: address.stateCode }),
            ...(address.postCode && { postcode: address.postCode }),
            ...(address.city && { city: address.city }),
          },
        }),
      ...(stripeIntent?.isPaymentIntent && { stripe_payment_intent_id: stripeIntent.id }),
      ...(taxNumber && { tax_number: taxNumber }),
    };

    try {
      yield post(`/ember/conversions/convert_to_paid_fin_for_platforms`, params);
      this.router.transitionTo('apps.app.checkout.success', this.appService.app.id);
      this.sendAnalyticsEvent({
        action: 'completed',
        object: 'fin_standalone_conversion',
        context: paramsForAnalytics,
      });
    } catch (err) {
      console.error(err);
      if (err && err?.jqXHR?.responseJSON && err?.jqXHR?.responseJSON[0]) {
        this.notificationsService.notifyError(err.jqXHR.responseJSON[0]);
      } else {
        this.notificationsService.notifyError(
          this.intl.t('signup.teams.pricing5.annual-plans.stripe.generic-error'),
        );
      }
      throw err;
    }
  }

  @keepLatestTask
  *getQuotes(): TaskGenerator<void> {
    let params = [
      {
        ...DEFAULT_REQUEST_PARAMS,
        copilotSeatCount: this.quoteService.copilotSeatCount,
        planIds: [Number(this.selectedPlanId)],
        pricingModelIdentifier: PricingModelIdentifier.FIN_FOR_PLATFORM,
        source: 'fin-standalone-checkout',
      },
    ];
    yield taskFor(this.quoteService.getQuotes).perform(params);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Signup::Teams::Pricing5::Checkouts::StandaloneCheckout': typeof StandaloneCheckout;
  }
}
